let _ = t => t,
  _t;
import { c as _c } from "react-compiler-runtime";
import { useCallback, useRef } from 'react';
import styledWithTheme from 'foundations-theming/utils';
import { useObserveTableElementRef } from '../../virtualization/hooks/useObserveTableElementRef';
import { LEFT_POSITION, RIGHT_POSITION, useColumnDragAndDrop } from '../../reordering/useColumnDragAndDrop';
import { COLUMN } from '../../virtualization/types/observerTypes';
import ContentComponentInner from './ContentComponentInner';
import { useFDTContextMenuConfig } from '../../contextMenu/context/FDTContextMenuConfigContext';
import { jsx as _jsx } from "react/jsx-runtime";
const StyledDropTarget = styledWithTheme.div(_t || (_t = _`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;

  border-width: ${0};
  border-style: ${0};
  border-radius: ${0};
  border-color: ${0};

  && [data-hide-when-not-hovered='true'] {
    display: none;
  }
  th:hover && [data-hide-when-not-hovered='true'],
  th:focus-within && [data-hide-when-not-hovered='true'] {
    display: block;
  }
`), ({
  theme
}) => theme.borderWidth[200], ({
  $hasNewTableHeader
}) => $hasNewTableHeader ? `none` : `dashed`, ({
  theme
}) => theme.borderRadius[300], ({
  theme,
  $isActiveDropTarget
}) => $isActiveDropTarget ? theme.color['border-core-hover'] : `transparent`);

/** This component is what is passed to UITable's `Content` prop, and internally implements column header drag and drop, and resizing
 * ResizableSortTH
 *   getContentComponent
 *     DraggableResizableHeaderContent
 *       Children
 *       ContentComponentInner
 *         ContextMenuHeaderTriggerButton
 *         ResizeAffordance
 */
export const DraggableResizableHeaderContent = t0 => {
  const $ = _c(46);
  const {
    arrowsNode,
    children,
    columnIndex,
    columnName,
    columnNamespace,
    reorderable,
    onColumnOrderChange,
    handleResize,
    originalWidth,
    onResizeEnd,
    resizable,
    hasNewTableHeader,
    sortDirection
  } = t0;
  const initialXRef = useRef(0);
  const handleObserveRef = useObserveTableElementRef();
  let t1;
  if ($[0] !== columnIndex || $[1] !== columnName || $[2] !== columnNamespace || $[3] !== hasNewTableHeader || $[4] !== onColumnOrderChange || $[5] !== reorderable) {
    t1 = {
      columnName,
      columnNamespace,
      columnIndex,
      onColumnOrderChange,
      reorderable,
      hasNewTableHeader
    };
    $[0] = columnIndex;
    $[1] = columnName;
    $[2] = columnNamespace;
    $[3] = hasNewTableHeader;
    $[4] = onColumnOrderChange;
    $[5] = reorderable;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  const {
    onDragStart,
    onDragEnter,
    onDragLeave,
    onDrop,
    onDragOver,
    onDrag,
    isActiveDropTarget,
    dropPosition
  } = useColumnDragAndDrop(t1);
  const contextMenuConfig = useFDTContextMenuConfig();
  let t2;
  if ($[7] !== columnName || $[8] !== columnNamespace || $[9] !== contextMenuConfig) {
    t2 = contextMenuConfig === null || contextMenuConfig === void 0 ? void 0 : contextMenuConfig.getIsContextMenuOpen({
      row: null,
      columnName,
      columnNamespace
    });
    $[7] = columnName;
    $[8] = columnNamespace;
    $[9] = contextMenuConfig;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  const isContextMenuOpen = t2;
  const isResizingRef = useRef(false);
  let t3;
  if ($[11] !== handleResize || $[12] !== onResizeEnd || $[13] !== originalWidth) {
    t3 = initiatingEvent => {
      initialXRef.current = initiatingEvent.clientX;
      isResizingRef.current = true;
      initiatingEvent.preventDefault();
      const handleMouseMove = t4 => {
        const {
          clientX
        } = t4;
        return handleResize(originalWidth + (clientX - initialXRef.current));
      };
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", event => {
        event.preventDefault();
        document.removeEventListener("mousemove", handleMouseMove);
        onResizeEnd();
        requestAnimationFrame(() => {
          isResizingRef.current = false;
        });
      }, {
        once: true
      });
    };
    $[11] = handleResize;
    $[12] = onResizeEnd;
    $[13] = originalWidth;
    $[14] = t3;
  } else {
    t3 = $[14];
  }
  const handleMouseDown = t3;
  let t4;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = event_0 => {
      if (isResizingRef.current) {
        event_0.stopPropagation();
      }
    };
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  const handleClick = t4;
  let t5;
  if ($[16] !== columnName || $[17] !== columnNamespace || $[18] !== reorderable) {
    t5 = reorderable && {
      "data-test-id": `drag-header-${columnNamespace}-${columnName}`
    };
    $[16] = columnName;
    $[17] = columnNamespace;
    $[18] = reorderable;
    $[19] = t5;
  } else {
    t5 = $[19];
  }
  const t6 = reorderable && !isContextMenuOpen;
  const t7 = hasNewTableHeader && isActiveDropTarget && dropPosition === LEFT_POSITION;
  const t8 = hasNewTableHeader && isActiveDropTarget && dropPosition === RIGHT_POSITION;
  let t9;
  if ($[20] !== arrowsNode || $[21] !== children || $[22] !== columnName || $[23] !== columnNamespace || $[24] !== handleMouseDown || $[25] !== hasNewTableHeader || $[26] !== reorderable || $[27] !== resizable || $[28] !== sortDirection || $[29] !== t7 || $[30] !== t8) {
    t9 = /*#__PURE__*/_jsx(ContentComponentInner, {
      arrowsNode: arrowsNode,
      columnName: columnName,
      columnNamespace: columnNamespace,
      onMouseDown: handleMouseDown,
      resizable: resizable,
      reorderable: reorderable,
      draggedLeft: t7,
      draggedRight: t8,
      hasNewTableHeader: hasNewTableHeader,
      sortDirection: sortDirection,
      children: children
    });
    $[20] = arrowsNode;
    $[21] = children;
    $[22] = columnName;
    $[23] = columnNamespace;
    $[24] = handleMouseDown;
    $[25] = hasNewTableHeader;
    $[26] = reorderable;
    $[27] = resizable;
    $[28] = sortDirection;
    $[29] = t7;
    $[30] = t8;
    $[31] = t9;
  } else {
    t9 = $[31];
  }
  let t10;
  if ($[32] !== columnIndex || $[33] !== handleObserveRef || $[34] !== hasNewTableHeader || $[35] !== isActiveDropTarget || $[36] !== onDrag || $[37] !== onDragEnter || $[38] !== onDragLeave || $[39] !== onDragOver || $[40] !== onDragStart || $[41] !== onDrop || $[42] !== t5 || $[43] !== t6 || $[44] !== t9) {
    t10 = /*#__PURE__*/_jsx(StyledDropTarget, Object.assign({
      $isActiveDropTarget: isActiveDropTarget,
      $hasNewTableHeader: hasNewTableHeader
    }, t5, {
      ref: handleObserveRef,
      "data-observer-type": COLUMN,
      "data-observer-id": columnIndex,
      draggable: t6,
      onClick: handleClick,
      onDragStart: onDragStart,
      onDrop: onDrop,
      onDragEnter: onDragEnter,
      onDragLeave: onDragLeave,
      onDragOver: onDragOver,
      onDrag: onDrag,
      children: t9
    }));
    $[32] = columnIndex;
    $[33] = handleObserveRef;
    $[34] = hasNewTableHeader;
    $[35] = isActiveDropTarget;
    $[36] = onDrag;
    $[37] = onDragEnter;
    $[38] = onDragLeave;
    $[39] = onDragOver;
    $[40] = onDragStart;
    $[41] = onDrop;
    $[42] = t5;
    $[43] = t6;
    $[44] = t9;
    $[45] = t10;
  } else {
    t10 = $[45];
  }
  return t10;
};